import { ChevronDown } from 'lucide-react';
import React, { useMemo } from 'react';
import { useState } from 'react';
import cn from 'classnames';
import { useRequest } from 'ahooks';
import { getOwnRecordDetail, getRecordDetail } from '../../services';
import { chunk, sortBy } from 'lodash-es';
import { IApiCustomsRecordVo } from '@/services/swagger/customs/models';
import dayjs from 'dayjs';
import { BaseDrawer } from '@/common/base/drawer';
import { Ellipse } from '@/common/base/ellipse';
import { useDialogVisible } from '@/hooks';

interface TableRowProps {
  data: Array<{
    label: string;
    value: string;
    showCopyButton?: boolean;
  }>;
  isLastRow?: boolean;
}

export const TableRow = ({ data, isLastRow = false }: TableRowProps) => {
  return (
    // 如果是父级元素的最后一个子元素，不显示 border-b
    <div
      className={cn('grid grid-cols-4 border-b border-gray-200', {
        'border-b-0': isLastRow,
      })}
    >
      {data.map((item, index) => (
        <React.Fragment key={item.label}>
          <div
            key={index}
            className={cn('bg-[#FAFAFA] p-3', {
              'border-r': true,
            })}
          >
            <span className="text-xs  text-[color:var(--800-fill-,#262626)] text-right">{item.label}</span>
          </div>
          <div
            className={cn('py-1 px-3', {
              // 文字垂直居中
              'flex items-center': true,
              // 如果只有一个元素，占满整个单元格
              'col-span-3': data.length === 1,
              // 如果是最后一个元素，不显示 border-r
              'border-r': data.length > 1 && index !== data.length - 1,
            })}
          >
            {/* 最多支持2行 */}
            <Ellipse className="text-xs text-gray-700" lineClamp={2} text={item.value || '-'} />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

interface TableSectionProps {
  title: string;
  children: React.ReactNode;
}

export const TableSection = ({ title, children }: TableSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="mb-8 border border-gray-200 border-b-0  overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center cursor-pointer justify-between bg-[#EFEFEF] px-3 py-[9px]"
      >
        <span className="text-xs font-medium text-gray-700">{title}</span>
        <ChevronDown size={14} className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && <div>{children}</div>}
    </div>
  );
};

interface RecordDetailProps {
  recordId: string;
  sourceType?: number;
}
export type CustomsRecordType = 'Import' | 'Export';

export function getRecordCountry(record: IApiCustomsRecordVo, target: CustomsRecordType = 'Import') {
  const recordType = target || record.recordType;
  const nameEn = recordType === 'Export' ? record.shpCountry : record.conCountry;
  return nameEn;
}
export function getRecordType(key?: CustomsRecordType, extra?: boolean) {
  const crtMap: { [key in CustomsRecordType]: string } = {
    Export: 'Export' + (extra ? 'Bill of Lading' : ''),
    Import: 'Import' + (extra ? 'B/L' : ''),
  };
  const value = key ? crtMap[key] : '';
  return value;
}

export function getFixedNoneZeroValue(value: string | number | undefined | null, fractionDigits: number = 2) {
  const v = Number(value);
  if (Number.isFinite(v) && v !== 0) {
    return v.toFixed(fractionDigits);
  }
  return '';
}

function getUnitPrice(record: IApiCustomsRecordVo) {
  const price = Number(record.valueOfGoodsUSD) / Number(record.itemQuantity ?? 1);
  return getFixedNoneZeroValue(price);
}

export const getCustomsRecordSorts = (record: IApiCustomsRecordVo) => {
  return [
    {
      title: 'Basic Information',
      items: [
        {
          label: 'Country Name',
          value: getRecordCountry(record),
        },
        {
          label: 'Import/Export',
          value: getRecordType(record.recordType as unknown as CustomsRecordType),
        },
        {
          label: 'Declaration Date',
          value: record.shpmtDate ? dayjs(record.shpmtDate).format('YYYY-MM-DD') : '',
        },
        {
          label: 'HS Code',
          value: record.hsCode,
        },
      ],
    },
    {
      title: 'Buyer Information',
      items: [
        {
          label: 'Buyer',
          searchType: 'import',
          value: (record.originConName ?? '') + (record.conOrgCode ? `(${record.conOrgCode})` : ''),
        },
        {
          label: 'Buyer Country',
          value: getRecordCountry(record, 'Import'),
        },
        {
          label: 'Buyer City',
          value: record.conCity,
        },
        {
          label: 'Buyer Address',
          value: record.conFullAddress,
        },
        {
          label: 'Buyer Postal Code',
          value: record.conPostalCode,
        },
        {
          label: 'Buyer Contact Person',
          value: record.consignee,
        },
        {
          label: 'Buyer Phone',
          value: record.conPhoneNum,
        },
        {
          label: 'Buyer Email',
          value: record.conEmail,
        },
        {
          label: 'Buyer Website',
          value: record.conWebUrl,
        },
      ],
    },
    {
      title: 'Supplier Information',
      items: [
        {
          label: 'Supplier',
          searchType: 'export',
          value: (record.originShpName ?? '') + (record.shpOrgCode ? `(${record.shpOrgCode})` : ''),
        },
        {
          label: 'Supplier Country',
          value: getRecordCountry(record, 'Export'),
        },
        {
          label: 'Supplier City',
          value: record.shpCity,
        },
        {
          label: 'Supplier Address',
          value: record.shpFullAddress,
        },
        {
          label: 'Supplier Postal Code',
          value: record.shpPostalCode,
        },
        {
          label: 'Supplier Contact Person',
          value: record.shipper,
        },
        {
          label: 'Supplier Phone',
          value: record.shpPhoneNum,
        },
        {
          label: 'Supplier Email',
          value: record.shpEmail,
        },
        {
          label: 'Supplier Website',
          value: record.shpWebUrl,
        },
      ],
    },
    {
      title: 'Product Information',
      items: [
        {
          label: 'Port of Loading',
          value: record.portOfLading,
        },
        {
          label: 'Port of Destination',
          value: record.portOfUnLading,
        },
        {
          label: record.conCountry === 'United States' ? 'Arrival Date' : 'Declaration Date',
          value:
            record.conCountry === 'United States'
              ? record.arrivalDate
                ? dayjs(record.arrivalDate).format('YYYY-MM-DD')
                : ''
              : record.shpmtDate
                ? dayjs(record.shpmtDate).format('YYYY-MM-DD')
                : '',
        },
        {
          label: 'Origin Country',
          value: record.originCountry,
        },
        {
          label: 'Product Description',
          value: record.goodsShipped,
        },
        {
          label: 'Amount (USD)',
          value: record.valueOfGoodsUSD,
          noneText: 'Not Public',
        },
        {
          label: 'Quantity',
          value: (record.itemQuantity ?? '') + (record.itemUnit ?? ''),
          noneText: 'Not Public',
        },
        {
          label: 'Unit Price',
          value: getUnitPrice(record),
          noneText: 'Not Public',
        },
        {
          label: 'Container Quantity',
          value: record.containerQuantity,
        },
        {
          label: 'Weight (kg)',
          value: getFixedNoneZeroValue(record.weightKg),
          noneText: 'Not Public',
        },
        {
          label: 'Net Weight' + (record.weightUnit ? `(${record.weightUnit})` : ''),
          value: getFixedNoneZeroValue(record.netWeight),
          noneText: 'Not Public',
        },
        {
          label: 'Gross Weight' + (record.weightUnit ? `(${record.weightUnit})` : ''),
          value: getFixedNoneZeroValue(record.grossWeight),
          noneText: 'Not Public',
        },
        {
          label: 'Industry',
          value: record.industry,
        },
        {
          label: 'Sub-Industry',
          value: record.subIndustry,
        },
      ],
    },
    {
      title: 'Other Information',
      items: [
        {
          label: 'Bill of Lading Number',
          value: record.billOfLadingNum,
        },
        {
          label: 'Declaration Number',
          value: record.declarationNum,
        },
        {
          label: 'Carrier',
          value: record.carrier,
        },
        {
          label: 'Shipping Company',
          value: record.shipCompany,
        },
        {
          label: 'Freight Terms',
          value: record.freightClause,
        },
        {
          label: 'Trade Type',
          value: record.tradeType,
        },
        {
          label: 'Transport Method',
          value: record.transportMethod,
        },
        {
          label: 'Transit Country',
          value: record.transitCountry,
        },
        {
          label: 'Trade Mode',
          value: record.tradeMode,
        },
        {
          label: 'Trade Term',
          value: record.tradeTerm,
        },
      ],
    },
  ];
};
export const RecordDetail = (props: RecordDetailProps) => {
  const { recordId } = props;
  const { data } = useRequest(async () => {
    return getRecordDetail({
      id: recordId,
    }).then(res => res.data);
  });
  const list = useMemo(() => {
    if (!data) {
      return [];
    }

    return getCustomsRecordSorts(data);
  }, [data]);

  return (
    <div className="space-y-4">
      {list.map((item, index) => {
        const chunkList = chunk(item.items as unknown[], 2);
        return (
          <TableSection key={index} title={item.title}>
            <div>
              {chunkList.map((item, index) => (
                <TableRow
                  key={index}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  data={item.map((cell: any) => ({
                    label: cell.label,
                    value: cell.value || '',
                  }))}
                />
              ))}
            </div>
          </TableSection>
        );
      })}
    </div>
  );
};

/**
 * sourceType 为0 时，使用这个组件
 * @param props
 * @returns
 */
export const RecordDetailB = (props: RecordDetailProps) => {
  const { recordId, sourceType } = props;
  const { data } = useRequest(async () => {
    return getOwnRecordDetail({
      recordId: recordId,
      sourceType: sourceType,
    }).then(res => res.data);
  });
  if (!data) return null;
  const recordSheetVOList = sortBy(data.recordSheetVOList || [], item => item.order);

  return (
    <div>
      {recordSheetVOList.map(item => {
        const { sheetNameEn, sheetNameCn, cellList } = item;

        const chunkList = chunk(cellList, 2);
        return (
          <TableSection key={sheetNameEn} title={sheetNameCn}>
            <div>
              {chunkList.map((item, index) => (
                <TableRow
                  key={index}
                  data={item.map(cell => ({
                    label: cell.cellNameCn,
                    value: cell.cellValue,
                  }))}
                />
              ))}
            </div>
          </TableSection>
        );
      })}
    </div>
  );
};

type RecordDetailModalProps = {
  visible: boolean;
  onClose: () => void;
} & Partial<RecordDetailProps>;

// 海关单据侧弹窗
export const PureRecordDetailModal = (props: RecordDetailModalProps) => {
  const { visible, onClose, recordId, sourceType } = props;
  return (
    <BaseDrawer title="Bill of lading data" open={visible} onOpenChange={() => onClose()}>
      <div className="px-4 py-4">
        {recordId && sourceType !== 1 ? <RecordDetailB recordId={recordId} sourceType={sourceType} /> : null}
        {recordId && sourceType === 1 ? <RecordDetail recordId={recordId} sourceType={sourceType} /> : null}
      </div>
    </BaseDrawer>
  );
};
export const RecordDetailModal = (props: RecordDetailModalProps) => {
  const open = useDialogVisible(props.visible);
  if (!open) {
    return null;
  }
  return <PureRecordDetailModal {...props} />;
};
